import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    ModeClassificationRule,
    ModeDefinitionCreatePayload,
    ModeFieldDefinition,
    ModeStatusDefinition,
} from '../api/domains';

const MAX_FIELDS = 20;
const MAX_STATUSES = 15;
const MAX_RULES = 50;
const MAX_PATTERN_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 500;
const SLUG_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;
const CODE_LIKE_SUBSTRINGS = ['{{', '}}', '`', 'import ', 'eval(', 'exec('] as const;

const STEPS = ['Basics', 'Fields', 'Statuses', 'Review'] as const;
type Step = (typeof STEPS)[number];

export type ModeBuilderWizardProps = {
    onSubmit: (payload: ModeDefinitionCreatePayload) => Promise<void>;
    submitting?: boolean;
    serverErrors?: string[];
    onStepChange?: () => void;
};

function slugify(value: string): string {
    const normalized = value
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
    if (!normalized) return 'field';
    if (/^[0-9]/.test(normalized)) return `f_${normalized}`;
    return normalized.slice(0, 40);
}

function defaultField(label = 'New Field'): ModeFieldDefinition {
    const name = slugify(label);
    return { name, type: 'string', label, required: false };
}

function defaultStatus(label = 'New Status'): ModeStatusDefinition {
    const key = slugify(label);
    return { key, label, terminal: false };
}

function rejectCodeLikeSubstrings(value: string, fieldName: string): string | null {
    for (const substring of CODE_LIKE_SUBSTRINGS) {
        if (value.includes(substring)) {
            return `${fieldName} contains forbidden code-like text: ${substring}`;
        }
    }
    return null;
}

function validateRegexPattern(pattern: string): string | null {
    if (!pattern.trim()) return 'Pattern is required';
    if (pattern.length > MAX_PATTERN_LENGTH) {
        return `Pattern must be at most ${MAX_PATTERN_LENGTH} characters`;
    }
    try {
        // eslint-disable-next-line no-new
        new RegExp(pattern);
    } catch {
        return 'Pattern is not a valid regular expression';
    }
    return null;
}

function validateSenderDomain(pattern: string): string | null {
    if (!pattern.trim()) return 'Domain is required';
    if (pattern !== pattern.toLowerCase()) return 'Domain must be lowercase';
    if (/[*+?[\]()|^$\\]/.test(pattern)) return 'Domain must be a hostname literal, not a regex';
    return null;
}

export function buildModeDefinitionPayload(state: {
    label: string;
    description: string;
    icon: string;
    color: string;
    fields: ModeFieldDefinition[];
    statuses: ModeStatusDefinition[];
    rules: ModeClassificationRule[];
}): ModeDefinitionCreatePayload {
    return {
        label: state.label.trim(),
        description: state.description.trim() || undefined,
        icon: state.icon.trim() || '✨',
        color: state.color.trim() || undefined,
        fields: state.fields,
        statuses: state.statuses,
        classification_rules: state.rules,
        extraction_hints: 'general_v1',
    };
}

export function validateWizardStep(
    step: Step,
    state: {
        label: string;
        description?: string;
        icon?: string;
        fields: ModeFieldDefinition[];
        statuses: ModeStatusDefinition[];
        rules: ModeClassificationRule[];
    },
): string[] {
    const errors: string[] = [];
    if (step === 'Basics' || step === 'Review') {
        if (!state.label.trim()) errors.push('Mode name is required');
        if (state.label.trim().length > 64) errors.push('Mode name must be at most 64 characters');
        const labelCodeLike = rejectCodeLikeSubstrings(state.label.trim(), 'Mode name');
        if (labelCodeLike) errors.push(labelCodeLike);
        const description = (state.description ?? '').trim();
        if (description.length > MAX_DESCRIPTION_LENGTH) {
            errors.push(`Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`);
        }
        const descriptionCodeLike = rejectCodeLikeSubstrings(description, 'Description');
        if (description && descriptionCodeLike) errors.push(descriptionCodeLike);
        const iconCodeLike = rejectCodeLikeSubstrings((state.icon ?? '').trim(), 'Icon');
        if ((state.icon ?? '').trim() && iconCodeLike) errors.push(iconCodeLike);
    }
    if (step === 'Fields' || step === 'Review') {
        if (state.fields.length === 0) errors.push('Add at least one field');
        if (state.fields.length > MAX_FIELDS) errors.push(`At most ${MAX_FIELDS} fields allowed`);
        const names = new Set<string>();
        state.fields.forEach((field) => {
            if (!SLUG_PATTERN.test(field.name)) {
                errors.push(`Field "${field.label}" needs a valid slug name`);
            }
            if (names.has(field.name)) errors.push(`Duplicate field name: ${field.name}`);
            names.add(field.name);
            if (field.type === 'enum' && (!field.enum_values || field.enum_values.length === 0)) {
                errors.push(`Enum field "${field.label}" needs at least one option`);
            }
        });
    }
    if (step === 'Statuses' || step === 'Review') {
        if (state.statuses.length === 0) errors.push('Add at least one status');
        if (state.statuses.length > MAX_STATUSES) errors.push(`At most ${MAX_STATUSES} statuses allowed`);
        const keys = new Set<string>();
        state.statuses.forEach((status) => {
            if (!SLUG_PATTERN.test(status.key)) {
                errors.push(`Status "${status.label}" needs a valid key`);
            }
            if (keys.has(status.key)) errors.push(`Duplicate status key: ${status.key}`);
            keys.add(status.key);
        });
    }
    if (step === 'Review') {
        if (state.rules.length > MAX_RULES) errors.push(`At most ${MAX_RULES} classification rules allowed`);
        const fieldNames = new Set(state.fields.map((field) => field.name));
        state.rules.forEach((rule, index) => {
            if (rule.type === 'subject_pattern') {
                const message = validateRegexPattern(rule.pattern);
                if (message) errors.push(`Rule ${index + 1}: ${message}`);
            } else if (rule.type === 'sender_domain') {
                const message = validateSenderDomain(rule.pattern);
                if (message) errors.push(`Rule ${index + 1}: ${message}`);
            } else if (!fieldNames.has(rule.pattern)) {
                errors.push(`Rule ${index + 1}: entity pattern must match a field name`);
            }
            if (rule.weight < 0 || rule.weight > 1) {
                errors.push(`Rule ${index + 1}: weight must be between 0 and 1`);
            }
        });
    }
    return errors;
}

export function rulePatternError(rule: ModeClassificationRule, fieldNames: Set<string>): string | null {
    if (rule.type === 'subject_pattern') {
        return validateRegexPattern(rule.pattern);
    }
    if (rule.type === 'sender_domain') {
        return validateSenderDomain(rule.pattern);
    }
    if (!fieldNames.has(rule.pattern)) {
        return 'Entity pattern must match a field name';
    }
    if (rule.weight < 0 || rule.weight > 1) {
        return 'Weight must be between 0 and 1';
    }
    return null;
}

const ModeBuilderWizard: React.FC<ModeBuilderWizardProps> = ({
    onSubmit,
    submitting = false,
    serverErrors = [],
    onStepChange,
}) => {
    const [stepIndex, setStepIndex] = useState(0);
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');
    const [icon, setIcon] = useState('✨');
    const [color, setColor] = useState('#0E7490');
    const [fields, setFields] = useState<ModeFieldDefinition[]>([
        defaultField('Entity'),
        defaultField('Status'),
    ]);
    const [statuses, setStatuses] = useState<ModeStatusDefinition[]>([
        { key: 'open', label: 'Open' },
        { key: 'done', label: 'Done', terminal: true },
    ]);
    const [rules, setRules] = useState<ModeClassificationRule[]>([]);
    const [showAdvancedRules, setShowAdvancedRules] = useState(false);
    const [localErrors, setLocalErrors] = useState<string[]>([]);
    const stepHeadingRef = useRef<HTMLParagraphElement>(null);

    const currentStep = STEPS[stepIndex];
    const state = useMemo(
        () => ({ label, description, icon, color, fields, statuses, rules }),
        [label, description, icon, color, fields, statuses, rules],
    );

    const stepErrors = useMemo(
        () => validateWizardStep(currentStep, state),
        [currentStep, state],
    );

    const reviewErrors = useMemo(() => validateWizardStep('Review', state), [state]);
    const reviewReady = reviewErrors.length === 0;

    useEffect(() => {
        stepHeadingRef.current?.focus();
    }, [stepIndex]);

    const moveField = (index: number, direction: -1 | 1) => {
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= fields.length) return;
        const next = [...fields];
        [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
        setFields(next);
    };

    const goNext = () => {
        const errors = validateWizardStep(currentStep, state);
        setLocalErrors(errors);
        if (errors.length > 0) return;
        onStepChange?.();
        setStepIndex((index) => Math.min(index + 1, STEPS.length - 1));
    };

    const goBack = () => {
        setLocalErrors([]);
        onStepChange?.();
        setStepIndex((index) => Math.max(index - 1, 0));
    };

    const handleSubmit = async () => {
        const errors = validateWizardStep('Review', state);
        setLocalErrors(errors);
        if (errors.length > 0) return;
        await onSubmit(buildModeDefinitionPayload(state));
    };

    const displayErrors = [...localErrors, ...serverErrors];

    const blockingStepErrors =
        localErrors.length === 0 && currentStep !== 'Review' ? stepErrors : [];
    const allDisplayErrors = [...blockingStepErrors, ...displayErrors];

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2">
                {STEPS.map((step, index) => (
                    <div
                        key={step}
                        className={`h-2 rounded-full transition-all ${
                            index === stepIndex
                                ? 'w-10 bg-teal-400'
                                : index < stepIndex
                                  ? 'w-6 bg-teal-400/50'
                                  : 'w-6 bg-white/15'
                        }`}
                        aria-hidden
                    />
                ))}
            </div>

            <p
                ref={stepHeadingRef}
                tabIndex={-1}
                className="text-xs text-teal-400 font-medium outline-none"
            >
                Step {stepIndex + 1} of {STEPS.length}: {currentStep}
            </p>

            {currentStep === 'Basics' && (
                <div className="grid gap-4 md:grid-cols-2">
                    <label className="block md:col-span-2">
                        <span className="text-xs text-slate-400 mb-2 block font-medium">Mode name</span>
                        <input
                            value={label}
                            onChange={(event) => setLabel(event.target.value)}
                            placeholder="Freelance Clients"
                            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-slate-100 outline-none focus:border-teal-400/50"
                        />
                    </label>
                    <label className="block md:col-span-2">
                        <span className="text-xs text-slate-400 mb-2 block font-medium">Description</span>
                        <textarea
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            placeholder="What kind of work should land in this mode?"
                            rows={3}
                            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-slate-100 outline-none focus:border-teal-400/50"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs text-slate-400 mb-2 block font-medium">Icon</span>
                        <input
                            value={icon}
                            onChange={(event) => setIcon(event.target.value)}
                            maxLength={16}
                            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-slate-100 outline-none focus:border-teal-400/50"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs text-slate-400 mb-2 block font-medium">Accent color</span>
                        <input
                            type="color"
                            value={color}
                            onChange={(event) => setColor(event.target.value)}
                            className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-2"
                        />
                    </label>
                </div>
            )}

            {currentStep === 'Fields' && (
                <div className="space-y-3">
                    {fields.map((field, index) => (
                        <div
                            key={`${field.name}-${index}`}
                            className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-12"
                        >
                            <label className="md:col-span-3 block">
                                <span className="text-xs text-slate-400 mb-1 block">Label</span>
                                <input
                                    value={field.label}
                                    onChange={(event) => {
                                        const next = [...fields];
                                        next[index] = {
                                            ...field,
                                            label: event.target.value,
                                            name: slugify(event.target.value) || field.name,
                                        };
                                        setFields(next);
                                    }}
                                    className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                />
                            </label>
                            <label className="md:col-span-3 block">
                                <span className="text-xs text-slate-400 mb-1 block">Slug</span>
                                <input
                                    value={field.name}
                                    onChange={(event) => {
                                        const next = [...fields];
                                        next[index] = { ...field, name: event.target.value };
                                        setFields(next);
                                    }}
                                    className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                />
                            </label>
                            <label className="md:col-span-2 block">
                                <span className="text-xs text-slate-400 mb-1 block">Type</span>
                                <select
                                    value={field.type}
                                    onChange={(event) => {
                                        const next = [...fields];
                                        next[index] = {
                                            ...field,
                                            type: event.target.value as ModeFieldDefinition['type'],
                                            enum_values:
                                                event.target.value === 'enum'
                                                    ? field.enum_values || ['option_a']
                                                    : undefined,
                                        };
                                        setFields(next);
                                    }}
                                    className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                >
                                    <option value="string">String</option>
                                    <option value="enum">Enum</option>
                                    <option value="date">Date</option>
                                    <option value="number">Number</option>
                                </select>
                            </label>
                            <label className="md:col-span-2 flex items-end gap-2 pb-2">
                                <input
                                    type="checkbox"
                                    checked={Boolean(field.required)}
                                    onChange={(event) => {
                                        const next = [...fields];
                                        next[index] = { ...field, required: event.target.checked };
                                        setFields(next);
                                    }}
                                />
                                <span className="text-xs text-slate-300">Required</span>
                            </label>
                            <div className="md:col-span-2 flex items-end justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => moveField(index, -1)}
                                    className="rounded-lg border border-white/10 px-2 py-1 text-xs"
                                >
                                    Up
                                </button>
                                <button
                                    type="button"
                                    onClick={() => moveField(index, 1)}
                                    className="rounded-lg border border-white/10 px-2 py-1 text-xs"
                                >
                                    Down
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFields(fields.filter((_, i) => i !== index))}
                                    className="rounded-lg border border-red-400/30 px-2 py-1 text-xs text-red-200"
                                >
                                    Remove
                                </button>
                            </div>
                            {field.type === 'enum' && (
                                <label className="md:col-span-12 block">
                                    <span className="text-xs text-slate-400 mb-1 block">
                                        Enum values (comma-separated)
                                    </span>
                                    <input
                                        value={(field.enum_values || []).join(', ')}
                                        onChange={(event) => {
                                            const enum_values = event.target.value
                                                .split(',')
                                                .map((entry) => entry.trim())
                                                .filter(Boolean);
                                            const next = [...fields];
                                            next[index] = { ...field, enum_values };
                                            setFields(next);
                                        }}
                                        className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                    />
                                </label>
                            )}
                        </div>
                    ))}
                    <button
                        type="button"
                        disabled={fields.length >= MAX_FIELDS}
                        onClick={() => setFields([...fields, defaultField(`Field ${fields.length + 1}`)])}
                        className="rounded-xl border border-dashed border-white/20 px-4 py-3 text-sm text-slate-200 hover:bg-white/5 disabled:opacity-50"
                    >
                        Add field ({fields.length}/{MAX_FIELDS})
                    </button>
                </div>
            )}

            {currentStep === 'Statuses' && (
                <div className="space-y-3">
                    {statuses.map((status, index) => (
                        <div
                            key={`${status.key}-${index}`}
                            className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-12"
                        >
                            <label className="md:col-span-4 block">
                                <span className="text-xs text-slate-400 mb-1 block">Label</span>
                                <input
                                    value={status.label}
                                    onChange={(event) => {
                                        const next = [...statuses];
                                        next[index] = {
                                            ...status,
                                            label: event.target.value,
                                            key: slugify(event.target.value) || status.key,
                                        };
                                        setStatuses(next);
                                    }}
                                    className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                />
                            </label>
                            <label className="md:col-span-4 block">
                                <span className="text-xs text-slate-400 mb-1 block">Key</span>
                                <input
                                    value={status.key}
                                    onChange={(event) => {
                                        const next = [...statuses];
                                        next[index] = { ...status, key: event.target.value };
                                        setStatuses(next);
                                    }}
                                    className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                />
                            </label>
                            <label className="md:col-span-2 flex items-end gap-2 pb-2">
                                <input
                                    type="checkbox"
                                    checked={Boolean(status.terminal)}
                                    onChange={(event) => {
                                        const next = [...statuses];
                                        next[index] = { ...status, terminal: event.target.checked };
                                        setStatuses(next);
                                    }}
                                />
                                <span className="text-xs text-slate-300">Terminal</span>
                            </label>
                            <div className="md:col-span-2 flex items-end justify-end">
                                <button
                                    type="button"
                                    onClick={() => setStatuses(statuses.filter((_, i) => i !== index))}
                                    className="rounded-lg border border-red-400/30 px-2 py-1 text-xs text-red-200"
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        disabled={statuses.length >= MAX_STATUSES}
                        onClick={() =>
                            setStatuses([...statuses, defaultStatus(`Status ${statuses.length + 1}`)])
                        }
                        className="rounded-xl border border-dashed border-white/20 px-4 py-3 text-sm text-slate-200 hover:bg-white/5 disabled:opacity-50"
                    >
                        Add status ({statuses.length}/{MAX_STATUSES})
                    </button>
                </div>
            )}

            {currentStep === 'Review' && (
                <div className="space-y-4">
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
                        <p className="font-semibold text-teal-300">{label || 'Untitled mode'}</p>
                        {description && <p className="mt-2 text-slate-400">{description}</p>}
                        <p className="mt-3 text-xs text-slate-400">
                            {fields.length} fields · {statuses.length} statuses · {rules.length} rules
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowAdvancedRules((open) => !open)}
                        className="text-sm text-purple-300 hover:text-purple-200"
                    >
                        {showAdvancedRules ? 'Hide' : 'Show'} classification rules (optional)
                    </button>

                    {showAdvancedRules && (
                        <div className="space-y-3">
                            {rules.map((rule, index) => {
                                const fieldNames = new Set(state.fields.map((field) => field.name));
                                const patternError = rulePatternError(rule, fieldNames);
                                return (
                                <div
                                    key={`rule-${index}`}
                                    className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-12"
                                >
                                    <label className="md:col-span-3 block">
                                        <span className="text-xs text-slate-400 mb-1 block">Type</span>
                                        <select
                                            value={rule.type}
                                            onChange={(event) => {
                                                const next = [...rules];
                                                next[index] = {
                                                    ...rule,
                                                    type: event.target.value as ModeClassificationRule['type'],
                                                };
                                                setRules(next);
                                            }}
                                            className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                        >
                                            <option value="sender_domain">sender_domain</option>
                                            <option value="subject_pattern">subject_pattern</option>
                                            <option value="entity">entity</option>
                                        </select>
                                    </label>
                                    <label className="md:col-span-5 block">
                                        <span className="text-xs text-slate-400 mb-1 block">Pattern</span>
                                        <input
                                            value={rule.pattern}
                                            onChange={(event) => {
                                                const next = [...rules];
                                                next[index] = { ...rule, pattern: event.target.value };
                                                setRules(next);
                                            }}
                                            maxLength={MAX_PATTERN_LENGTH}
                                            aria-invalid={Boolean(patternError)}
                                            aria-describedby={patternError ? `rule-pattern-error-${index}` : undefined}
                                            className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                        />
                                        {patternError && (
                                            <span
                                                id={`rule-pattern-error-${index}`}
                                                className="mt-1 block text-xs text-red-200"
                                            >
                                                {patternError}
                                            </span>
                                        )}
                                    </label>
                                    <label className="md:col-span-2 block">
                                        <span className="text-xs text-slate-400 mb-1 block">Weight</span>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.1}
                                            value={rule.weight}
                                            onChange={(event) => {
                                                const next = [...rules];
                                                next[index] = {
                                                    ...rule,
                                                    weight: Number.parseFloat(event.target.value) || 0,
                                                };
                                                setRules(next);
                                            }}
                                            className="w-full rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm"
                                        />
                                    </label>
                                    <div className="md:col-span-2 flex items-end justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setRules(rules.filter((_, i) => i !== index))}
                                            className="rounded-lg border border-red-400/30 px-2 py-1 text-xs text-red-200"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                                );
                            })}
                            <button
                                type="button"
                                disabled={rules.length >= MAX_RULES}
                                onClick={() =>
                                    setRules([
                                        ...rules,
                                        { type: 'sender_domain', pattern: 'example.com', weight: 0.5 },
                                    ])
                                }
                                className="rounded-xl border border-dashed border-white/20 px-4 py-3 text-sm text-slate-200 hover:bg-white/5 disabled:opacity-50"
                            >
                                Add rule ({rules.length}/{MAX_RULES})
                            </button>
                        </div>
                    )}
                </div>
            )}

            {allDisplayErrors.length > 0 && (
                <div
                    role="alert"
                    className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100"
                >
                    <ul className="list-disc pl-5 space-y-1">
                        {allDisplayErrors.map((error, index) => (
                            <li key={`${index}-${error}`}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-2">
                <button
                    type="button"
                    onClick={goBack}
                    disabled={stepIndex === 0 || submitting}
                    className="min-h-[44px] rounded-xl border border-white/10 px-5 py-2.5 text-sm text-slate-200 disabled:opacity-50"
                >
                    Back
                </button>
                {stepIndex < STEPS.length - 1 ? (
                    <button
                        type="button"
                        onClick={goNext}
                        disabled={submitting}
                        className="min-h-[44px] rounded-xl bg-gradient-to-r from-teal-500 to-teal-400 px-6 py-2.5 text-sm font-semibold text-slate-900 disabled:opacity-50"
                    >
                        Next
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || !reviewReady}
                        className="min-h-[44px] rounded-xl bg-gradient-to-r from-purple-500 to-purple-400 px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                    >
                        {submitting ? 'Creating…' : 'Create mode'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default ModeBuilderWizard;
